/**
 * Поток игрока, шаг 1: ввод ника (PIN уже известен — из ввода главной или `?pin=`).
 * После сабмита монтируется PlayerGame, который открывает WS и шлёт join с ником.
 * Разделение нужно, чтобы не подключаться к комнате до ввода ника.
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPin } from '../components/ui-helpers';
import { clearPlayer, loadPlayer, savePlayer } from '../activeSession';
import { PlayerGame } from './PlayerGame';

export interface PlayerScreenProps {
  pin: string;
  onExit: () => void;
}

export function PlayerScreen({ pin, onExit }: PlayerScreenProps): JSX.Element {
  const { t } = useTranslation();
  // Session-resume: восстанавливаем сохранённый ник (после F5), чтобы
  // переподключиться тем же ником → сервер переиспользует playerID (счёт сохранится).
  const [nick, setNick] = useState(() => loadPlayer()?.nick ?? '');
  const [submitted, setSubmitted] = useState(() => loadPlayer() !== null);

  function handleExit(): void {
    clearPlayer();
    onExit();
  }

  /**
   * Фаза 13 (Эпик A2b): сервер отклонил nick (`nick_taken`) — возвращаемся к
   * форме ввода. `suggestion` (тап по кандидату) подставляется в поле;
   * авто-join сознательно НЕ делаем — следующий join инициирует игрок.
   * Resume-запись чистим: сессия не началась, автоповтор того же ника не нужен.
   */
  function handleNickRejected(suggestion: string | null): void {
    clearPlayer();
    if (suggestion !== null) setNick(suggestion);
    setSubmitted(false);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = nick.trim();
    if (!trimmed) return;
    setNick(trimmed);
    savePlayer(pin, trimmed);
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <PlayerGame pin={pin} nick={nick} onExit={handleExit} onNickRejected={handleNickRejected} />
    );
  }

  return (
    <main className="card" aria-labelledby="join-title">
      <button type="button" className="back" onClick={handleExit}>
        {t('player.back')}
      </button>
      <h1 id="join-title" className="block-title">
        {t('player.joinTitle')}
      </h1>
      <p className="muted">
        {t('player.codeLabel')} <strong>{formatPin(pin)}</strong>
      </p>

      <form className="entry" onSubmit={handleSubmit} noValidate>
        <label className="field-label" htmlFor="nick">
          {t('player.nickLabel')}
        </label>
        <input
          id="nick"
          className="text-input"
          type="text"
          maxLength={24}
          placeholder={t('player.nickPlaceholder')}
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          autoFocus
        />
        <button type="submit" className="cta" disabled={!nick.trim()}>
          {t('player.joinBtn')}
        </button>
      </form>
    </main>
  );
}
